import { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Button from "../../../components/button";
import FitnessToolsResultCard from "../../../components/cards/FitnessToolsResultCard";
import Footer from "../../../components/footer";
import Header from "../../../components/header";
import Input from "../../../components/input";
import Loader from "../../../components/loader";
import OtherCalculatorSection from "../../../components/OtherCalculatorSection";
import AISkeleton from "../../../components/skeleton/AISkeleton";
import { useTranslate } from "../../../context/TranslationContext";
import useChartPeriod from "../../../hooks/useChartPeriod";
import {
  useCreateORMMutation,
  useGetORMQuery,
  useUpdateORMMutation,
} from "../../../redux/Apis/Calculator/ORM";
import { useCreateAISuggestionMutation } from "../../../redux/Apis/TrackLecture";
import { useStaticData } from "../../../utils/data";
import { formatAISuggestion } from "../../../utils/helper";

const OneRepMax = () => {
  const { t } = useTranslate("oneRepMax");
  const lang = useSelector((state) => state.language.lang);
  const navigate = useNavigate();
  const { CALCULATORS, CALCULATOR_DATA } = useStaticData();
  const { user } = useSelector((state) => state.authSlice);
  const location = useLocation();
  const [exercise, setExercise] = useState("Squat");
  const [weightLifted, setWeightLifted] = useState("");
  const [reps, setReps] = useState("");
  const [weightUnit, setWeightUnit] = useState("kg");
  const [result, setResult] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [createORM] = useCreateORMMutation();
  const [updateORM] = useUpdateORMMutation();
  const [aiSuggestion, setAiSuggestion] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [highlightAI, setHighlightAI] = useState(false);
  const aiRef = useRef(null);
  const [createAISuggestion] = useCreateAISuggestionMutation();
  const {
    data: ormHistoryData,
    refetch: refetchORM,
    isLoading,
  } = useGetORMQuery({ lang }, { skip: !user });
  const courseState = location?.state || {};
  // const templateId = courseState?.templateId;
  const courseId = courseState?.course;
  const sectionId = courseState?.sectionId || courseState?.section;
  const { period, dateRange, handlePeriodChange } = useChartPeriod();
  const { startDate, endDate } = dateRange;
  const [showSkeleton, setShowSkeleton] = useState(false);
  const {
    data: ormData,
    refetch: refetchOrmData,
    isLoading: loadingORM,
  } = useGetORMQuery(
    { startdate: startDate, enddate: endDate, lang },
    { skip: !startDate || !endDate || !user },
  );

  useEffect(() => {
    if (aiLoading) {
      const timer = setTimeout(() => setShowSkeleton(true), 200);
      return () => clearTimeout(timer);
    } else {
      setShowSkeleton(false);
    }
  }, [aiLoading]);

  useEffect(() => {
    if ((aiLoading || aiSuggestion) && aiRef.current) {
      setTimeout(() => {
        const y =
          aiRef.current.getBoundingClientRect().top + window.pageYOffset - 110; // header height offset

        window.scrollTo({
          top: y,
          behavior: "smooth",
        });

        setHighlightAI(true);
        setTimeout(() => setHighlightAI(false), 3000);
      }, 300);
    }
  }, [aiLoading, aiSuggestion]);

  useEffect(() => {
    if (user) {
      refetchORM();
    }
  }, [user, refetchORM]);

  useEffect(() => {
    if (startDate && endDate && user) {
      refetchOrmData();
    }
  }, [startDate, endDate, user, refetchOrmData]);

  // useEffect(() => {
  //   setMetaTags(
  //     "Traineego | Buy or Create Automated Courses & Tracks for Fitness & Wellness",
  //     "Create, manage, and sell your own courses and tracks, or buy high-quality content. Empower your career through automation and passive income today!",
  //   );
  // }, []);

  useEffect(() => {
    if (ormHistoryData) {
      setHistoryData(ormHistoryData.data || []);
    }
  }, [ormHistoryData]);

  const calculateOneRepMax = () => {
    const weight = parseFloat(weightLifted);
    const repCount = parseInt(reps);

    if (!weight || weight <= 0 || !repCount || repCount <= 0) {
      toast.error(t("toasts.invalidInput"));
      return;
    }

    const oneRepMax = weight * (1 + repCount / 30);
    const oneRepMaxFixed = parseFloat(oneRepMax.toFixed(2));

    const result = {
      result: oneRepMaxFixed,
      exercise,
    };

    setResult(result);
    toast.success(t("toasts.calculateSuccess"));
    return result;
  };

  const formatDateTime = (timestamp) => {
    if (!timestamp) return t("history.notCalculated");
    const date = new Date(timestamp);
    return date.toLocaleString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const formatHistoryEntry = (entry) => {
    const createdAt = new Date(entry.createdAt).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
    return (
      <div className="d-flex justify-content-between align-items-center">
        <p className="mb-0">{createdAt}</p>
        <p className="mb-0 fw-bold">{entry.result} kg</p>
      </div>
    );
  };

  const generateAICoach = async (inputResult) => {
    try {
      // if (!courseId || !sectionId) return;

      setAiLoading(true);

      const aiPayload = {
        courseid: courseId,
        sectionid: sectionId,
        learnerid: user._id,
        type: "One Rep Max",
        result: [
          {
            weightLifted: inputResult.weight_lifted,
            reps: inputResult.number_of_reps,
            exercise: inputResult.lift,
            result: inputResult.result,
            weightUnit: weightUnit,
          },
        ],
      };

      const res = await createAISuggestion({
        payload: aiPayload,
        params: { lang },
      }).unwrap();

      const suggestion = res?.data?.suggestion;
      if (suggestion) setAiSuggestion(suggestion);
    } catch (err) {
      console.log("AI error", err);
    } finally {
      setAiLoading(false);
    }
  };

  const handleSaveResult = async (type, result) => {
    if (!result?.result) {
      toast.error(t("toasts.calculateFirst"));
      return;
    }

    if (!user) {
      toast.error(t("toasts.loginRequired"));
      // navigate("/auth/login");
      return;
    }

    try {
      const payload = {
        weight_lifted: weightLifted,
        number_of_reps: reps,
        result: result.result,
        lift: result.exercise,
      };
      if (courseId) payload.course = courseId;
      const response = await createORM({ payload, params: { lang } }).unwrap();
      toast.success(response.message || t("toasts.saveSuccess"));
      refetchORM();
      refetchOrmData();
      // if (courseId && sectionId && templateId) {
      await generateAICoach(payload);
      // }
    } catch (error) {
      console.error("Error saving OneRepMax:", error);
      toast.error(t("toasts.saveFail"));
    }
  };

  const handleDeleteLast = async () => {
    try {
      const latestEntry = [...historyData].sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
      )[0];
      if (latestEntry) {
        const payload = {
          id: latestEntry._id,
          isactive: false,
          params: { lang },
        };
        await updateORM(payload).unwrap();
        toast.success(t("toasts.deleteSuccess"));
        refetchORM();
        refetchOrmData();
      }
    } catch (error) {
      console.error("Error deleting last OneRepMax:", error);
      toast.error(t("toasts.deleteFail"));
    }
  };

  const toggleHistory = () => {
    setShowHistory((prev) => !prev);
  };

  if (isLoading || loadingORM) {
    return <Loader loading={true} />;
  }

  return (
    <>
      <Helmet>
        <title>
          One Rep Max Calculator | Estimate Your Maximum Lift | Traineego
        </title>

        <meta
          name="description"
          content="Estimate your one rep max for bench press, squat, deadlift, and other exercises using Traineego's strength calculator."
        />

        <meta name="robots" content="index,follow" />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: "One Rep Max Calculator",
            url: "https://traineego.com/calculators/one-rep-max",
            description:
              "Estimate your one rep max for bench press, squat, deadlift, and other exercises using Traineego's strength calculator.",
            publisher: {
              "@type": "Organization",
              name: "Traineego",
              url: "https://traineego.com",
            },
          })}
        </script>
      </Helmet>
      <Header />
      <section className="banner" style={{ textAlign: "left" }}>
        <div className="container py-5">
          <div className="row">
            <div className="col-12 text-center">
              <h1 className="text-uppercase">{t("title")}</h1>
              <p className="mb-0 p-lg l-grey-text">{t("subtitle")}</p>
            </div>
          </div>

          <div className="row d-flex">
            <div className="col-md-12 mt-4 p-5 fitness-card">
              <div className="row">
                <div className="col-lg-8">
                  <h4 className="text-uppercase mb-4">
                    {t("calculateSectionTitle")}
                  </h4>

                  <div className="row">
                    <div className="col-md-6">
                      <Input
                        label={t("inputs.weightLifted")}
                        placeholder={t("inputs.weightPlaceholder")}
                        type="number"
                        required={true}
                        value={weightLifted}
                        onChange={(e) => setWeightLifted(e.target.value)}
                      />
                    </div>
                    <div className="col-md-6">
                      <Input
                        label={t("inputs.unit")}
                        type="select"
                        options={CALCULATOR_DATA.WEIGHT_UNITS}
                        value={weightUnit}
                        onChange={(e) => setWeightUnit(e.target.value)}
                        required={true}
                      />
                    </div>
                  </div>

                  <div className="row mt-3">
                    <div className="col-md-6">
                      <Input
                        label={t("inputs.reps")}
                        placeholder="e.g., 5"
                        type="number"
                        required={true}
                        value={reps}
                        onChange={(e) => setReps(e.target.value)}
                      />
                    </div>
                    <div className="col-md-6">
                      <Input
                        label={t("inputs.lift")}
                        type="select"
                        options={CALCULATOR_DATA.LIFT}
                        value={exercise}
                        onChange={(e) => setExercise(e.target.value)}
                        required
                      />
                    </div>
                  </div>

                  <div className="row mt-5">
                    <div className="fitness-branded col-12 py-2 px-4 d-flex justify-content-between align-items-center">
                      <p className="grey-text m-0">{t("calculateBox.label")}</p>
                      <Button
                        text={t("calculateBox.button")}
                        onClick={calculateOneRepMax}
                      />
                    </div>
                  </div>
                  {location.state?.returnTo && result && (
                    <div className="text-center mt-3">
                      <Button
                        text={t("continueLearning")}
                        className="site-btn border-btn"
                        style={{ width: "100%", maxWidth: "200px" }}
                        onClick={() =>
                          navigate(location.state.returnTo, {
                            state: {
                              ...location.state,
                              resultKey: `${location.state.sectionIndex}-${location.state.lectureIndex}-ORM`,
                              resultData: {
                                type: "ORM",
                                result: `${result.result} ${weightUnit} (Estimated 1RM for ${result.exercise})`,
                                value: `${result.result} ${weightUnit}`,
                                timestamp: new Date().toISOString(),
                              },
                            },
                          })
                        }
                      />
                    </div>
                  )}
                </div>

                <div className="col-12 col-lg-4 mt-4 mt-lg-0">
                  <div
                    className="h-100 p-3 p-md-4 d-flex flex-column justify-content-center"
                    style={{
                      border: "1px solid white",
                      borderRadius: "1rem",
                      background: "rgba(112,112,112,0.4)",
                    }}
                  >
                    {result ? (
                      <h6>
                        {t("resultBox.calculated")} {result.exercise}{" "}
                        {t("resultBox.is")} {result.result} {weightUnit}
                      </h6>
                    ) : (
                      <h6>{t("resultBox.title")}</h6>
                    )}
                    <p className="p-sm">{t("resultBox.description")}</p>
                    <div
                      className="bg-white w-100 p-3 text-center mt-3"
                      style={{
                        border: "0.5px solid gray",
                        height: "auto",
                        borderRadius: "0.5rem",
                      }}
                    >
                      <p className="m-0 text-black">
                        {t("resultBox.noteMain")}
                      </p>
                      <p className="m-0 grey-text p-sm">
                        {t("resultBox.noteSub")}
                      </p>
                      <Button
                        text={t("resultBox.startTraining")}
                        to="/classes"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <FitnessToolsResultCard
              type="ORM"
              result={
                result
                  ? {
                      result: result.result,
                      timestamp: new Date().toISOString(),
                      exercise: result.exercise,
                    }
                  : null
              }
              // historyData={historyData.filter(
              //   (entry) => entry.lift === exercise
              // )}
              historyData={historyData}
              showHistory={showHistory}
              toggleHistory={toggleHistory}
              handleSaveResult={handleSaveResult}
              handleDeleteLast={handleDeleteLast}
              formatHistoryEntry={formatHistoryEntry}
              formatDateTime={formatDateTime}
              chartData={ormData?.data || []}
              period={period}
              handlePeriodChange={handlePeriodChange}
            />

            {(aiLoading || aiSuggestion) && (
              <div
                className={`ai-suggestion-box ${highlightAI ? "ai-highlight" : ""}`}
                ref={aiRef}
              >
                <h4 className="text-white mb-3">
                  {/* <FontAwesomeIcon icon={faRobot} color="var(--primaryColor)" />{" "} */}
                  <img
                    src="https://traineego.com/assets/images/favicon-2026.ico"
                    alt="AI Coach"
                    style={{
                      width: "45px",
                      marginRight: "8px",
                      borderRadius: "25px",
                    }}
                  />
                  {t("aiSuggestion.title")}
                </h4>

                {showSkeleton ? (
                  // <p style={{ color: "#aaa" }}>
                  //   <Spinner size="sm" /> {t("aiSuggestion.loading")}
                  // </p>
                  <AISkeleton />
                ) : (
                  <div
                    style={{ color: "#d1d1d6", lineHeight: "1.7" }}
                    dangerouslySetInnerHTML={{
                      __html: formatAISuggestion(aiSuggestion),
                    }}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </section>
      <OtherCalculatorSection
        data={CALCULATORS.filter(
          (item) => item.route !== window.location.pathname,
        )}
      />
      <Footer />
    </>
  );
};

export default OneRepMax;
